import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "../../../contexts/AuthContext";
import Sidebar from "../../UI/Sidebar";
import { LoadingProgress, ChartCard } from "../Common";
import { useSequentialLoading } from "../Common/hooks/useSequentialLoading";
import SimpleStatCard from "../Common/components/SimpleStatCard";
import UserFilter from "../Common/components/UserFilter";
import SkeletonLoader from "../../UI/SkeletonLoader";
import {
  DMPieChart,
  DMStackedChart,
  greenColor,
  orangeColor,
  redColor,
  yellowColor,
  multicolor,
} from "../../UI/DMCharts";
import {
  _callPrepsCS,
  _accountByProducts,
  _pausedProducts,
  _overdueInvoices,
  _completionOfSAItasks,
} from "../../Utils/CSInit";
import { openURLInNewTab } from "../../Utils";
import * as queries from "./directorsQueries";
import Loader from "../../UI/Loader";
import ErrorMessage from "../../UI/ErrorMessage";
import OpenTasksTable from "../../UI/OpenTasksTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip as ReactTooltip } from "react-tooltip";
import {
  faInfoCircle,
  faSync,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";

const DirectorsDashboard = () => {
  const { owId, sidebar, hidediv } = useParams();
  const navigate = useNavigate();
  const context = useAuthContext();

  // Add render count tracking for debugging
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;

  // State for user filtering
  const [selectedDirector, setSelectedDirector] = useState(owId || null);
  const [directors, setDirectors] = useState([]);
  const [loadingDirectors, setLoadingDirectors] = useState(false);

  // State for open tasks (loaded separately)
  const [openTasks, setOpenTasks] = useState([]);
  const [loadingOpenTasks, setLoadingOpenTasks] = useState(false);

  // Use refs to store stable chart data and prevent re-renders
  const chartDataRef = useRef({});
  const lastDataHashRef = useRef("");

  // Debug effect for tracking re-renders - only log when state changes
  useEffect(() => {
    console.log("🎨 DirectorsDashboard: Component state changed", {
      renderCount: renderCountRef.current,
      selectedDirector,
      contextReady: context?.ready,
      directorsCount: directors.length,
      openTasksCount: openTasks.length,
    });
  }, [selectedDirector, context?.ready, directors.length, openTasks.length]); // Only log when these actually change

  // Load directors list when context is ready
  useEffect(() => {
    if (!context?.ready || directors.length > 0) return;

    setLoadingDirectors(true);
    queries
      .getDirectorsList(context)
      .then((result) => {
        setDirectors(result);
      })
      .catch(console.error)
      .finally(() => setLoadingDirectors(false));
  }, [context?.ready, directors.length]);

  // Memoize queryParams to prevent unnecessary re-renders
  // Only update when selectedDirector actually changes value, not reference
  const queryParams = useMemo(
    () => ({
      selectedDirector: selectedDirector || null,
    }),
    [selectedDirector]
  );

  const {
    data,
    loading,
    error,
    progress,
    handleRetry,
    isLoading,
    isInitialized,
  } = useSequentialLoading({
    queries: {
      activeMemberships: queries.getActiveMemberships,
      darTasksThisMonth: queries.getDARTasksThisMonth,
      darTasksThisWeek: queries.getDARTasksThisWeek,
      upsellsThisMonth: queries.getUpsellsThisMonth,
      noOpenActivities: queries.getAccountsWithNoOpenActivities,
      overdueTasks: queries.getOverdueTasks,
      tasksCompletedThisWeek: queries.getTasksCompletedThisWeek,
      tasksCompletedThisMonth: queries.getTasksCompletedThisMonth,
      accountsByProduct: queries.getAccountsByProduct,
      overdueInvoices: queries.getOverdueInvoices,
      pausedProducts: queries.getPausedProducts,
      completionOfSAItasks: queries.getCompletionOfSAItasks,
      upcomingGMOrderExpirations: queries.getUpcomingGMOrderExpirations,
      // New cards queries
      newClientsThisMonth: queries.getNewClientsThisMonth,
      noUpSellDiscussion: queries.getNoUpSellDiscussion,
      noPaymentConfirmed: queries.getNoPaymentConfirmed,
      highRiskAccounts: queries.getHighRiskAccounts,
    },
    context: context,
    queryParams: queryParams,
  });

  // Debug logging - only log when loading state changes
  useEffect(() => {
    if (isInitialized && !isLoading) {
      console.log("✅ DirectorsDashboard: All queries completed", {
        dataKeys: Object.keys(data || {}),
        errorKeys: Object.keys(error || {}),
        renderCount: renderCountRef.current,
      });
    }
  }, [isInitialized, isLoading]);

  // Check if ALL data is ready
  const isAllDataReady = useMemo(() => {
    const mainDataReady = isInitialized && !isLoading;
    const allReady = mainDataReady;

    console.log("📊 Directors Dashboard - Data readiness check:", {
      mainDataReady,
      allReady,
      renderCount: renderCountRef.current,
    });

    return allReady;
  }, [isInitialized, isLoading]);

  // Load open tasks separately after main dashboard loads
  useEffect(() => {
    if (!context?.ready || !isAllDataReady) return;

    const loadOpenTasks = async () => {
      setLoadingOpenTasks(true);
      try {
        const tasks = await queries.getOpenTasks(context, selectedDirector);
        setOpenTasks(tasks);
      } catch (error) {
        console.error("Error loading open tasks:", error);
        setOpenTasks([]);
      } finally {
        setLoadingOpenTasks(false);
      }
    };

    const timeoutId = setTimeout(loadOpenTasks, 500);
    return () => clearTimeout(timeoutId);
  }, [context?.ready, isAllDataReady, selectedDirector]);

  // Stable chart data processing - only update when data actually changes
  const processedChartData = useMemo(() => {
    const currentDataHash = JSON.stringify({
      accountsByProduct: data.accountsByProduct,
      upcomingGMOrderExpirations: data.upcomingGMOrderExpirations,
      pausedProducts: data.pausedProducts,
      overdueInvoices: data.overdueInvoices,
      completionOfSAItasks: data.completionOfSAItasks,
      selectedDirector,
    });

    // Only reprocess if data actually changed
    if (
      currentDataHash === lastDataHashRef.current &&
      Object.keys(chartDataRef.current).length > 0
    ) {
      return chartDataRef.current;
    }

    lastDataHashRef.current = currentDataHash;

    // Process chart data
    const chartData = {};

    // Accounts by Product (Bar Chart)
    if (data.accountsByProduct) {
      const productData = {};
      Object.keys(data.accountsByProduct).forEach((key) => {
        if (
          key !== "breakdown" &&
          key !== "count" &&
          key !== "data" &&
          typeof data.accountsByProduct[key] === "number"
        ) {
          productData[key] = data.accountsByProduct[key];
        }
      });

      const productNames = Object.keys(productData).sort(
        (a, b) => productData[b] - productData[a]
      );

      chartData.accountsByProduct = {
        labels: productNames,
        datasets: [
          {
            label: "Number of Accounts",
            data: productNames.map((name) => productData[name]),
            backgroundColor: productNames.map((_, index) => {
              const baseColor = multicolor[index % multicolor.length];
              return `linear-gradient(135deg, ${baseColor}dd, ${baseColor}bb)`;
            }),
            borderColor: "white",
            borderWidth: 1,
          },
        ],
      };
    }

    // Stacked charts with consistent structure
    const createStackedData = (sourceData) => ({
      labels: ["Tasks"],
      datasets: [
        {
          label: "On Time",
          data: [sourceData?.Green || 0],
          backgroundColor: `linear-gradient(135deg, ${greenColor}dd, ${greenColor}bb)`,
          borderColor: "white",
          borderWidth: 1,
        },
        {
          label: "Needs Attention",
          data: [sourceData?.Yellow || 0],
          backgroundColor: `linear-gradient(135deg, ${yellowColor}dd, ${yellowColor}bb)`,
          borderColor: "white",
          borderWidth: 1,
        },
        {
          label: "Overdue",
          data: [sourceData?.Red || 0],
          backgroundColor: `linear-gradient(135deg, ${redColor}dd, ${redColor}bb)`,
          borderColor: "white",
          borderWidth: 1,
        },
      ],
    });

    if (data.upcomingGMOrderExpirations) {
      chartData.upcomingGMOrderExpirations = createStackedData(
        data.upcomingGMOrderExpirations
      );
    }

    if (data.pausedProducts) {
      chartData.pausedProducts = createStackedData(data.pausedProducts);
    }

    if (data.overdueInvoices) {
      chartData.overdueInvoices = createStackedData(data.overdueInvoices);
    }

    if (data.completionOfSAItasks) {
      chartData.completionOfSAItasks = createStackedData(
        data.completionOfSAItasks
      );
    }

    chartDataRef.current = chartData;
    return chartData;
  }, [
    data.accountsByProduct,
    data.upcomingGMOrderExpirations,
    data.pausedProducts,
    data.overdueInvoices,
    data.completionOfSAItasks,
    selectedDirector,
  ]);

  // Memoized event handlers
  const handleChartClick = useCallback(
    (type, color) => {
      navigate(`/directorsDashboard/data/${type}/${color}`);
    },
    [navigate]
  );

  const handleDirectorChange = useCallback((directorId) => {
    setSelectedDirector(directorId);
  }, []);

  // Effect to update selectedDirector when owId changes
  useEffect(() => {
    if (owId !== undefined) {
      setSelectedDirector(owId === "0" ? null : owId);
    }
  }, [owId]);

  // Show loading state
  if (!context?.ready || !isAllDataReady) {
    return (
      <div style={{ height: "100vh", overflow: "auto" }}>
        <div className="container">
          <Sidebar activeItem="directors" />
          <div className="content dashboard main-content">
            <div className="dashboard-header">
              <h1>Directors Dashboard</h1>
              <UserFilter
                directors={directors}
                selectedDirector={selectedDirector}
                onDirectorChange={setSelectedDirector}
                loading={loadingDirectors}
                placeholder="All Directors"
              />
            </div>

            <div className="dashboard-main">
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100vh",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <Loader />
                  <div style={{ marginTop: "20px" }}>
                    Loading Directors Dashboard...
                  </div>
                  {isLoading && <LoadingProgress progress={progress} />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state - check if any query has an error
  const hasErrors = Object.values(error).some(
    (err) => err !== null && err !== false
  );
  if (hasErrors) {
    console.error("Dashboard errors:", error);
    return (
      <div style={{ height: "100vh", overflow: "auto" }}>
        <div className="container">
          <Sidebar activeItem="directors" />
          <div className="content dashboard main-content">
            <div className="dashboard-header">
              <h1>Directors Dashboard</h1>
              <UserFilter
                directors={directors}
                selectedDirector={selectedDirector}
                onDirectorChange={setSelectedDirector}
                loading={loadingDirectors}
                placeholder="All Directors"
              />
            </div>

            <div className="dashboard-main">
              <ErrorMessage
                message="Failed to load directors data. Please try again."
                onRetry={() => window.location.reload()}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ height: "100vh", overflow: "auto" }}
      key="directors-dashboard"
    >
      <style jsx>
        {`
          .container {
            display: flex;
            min-height: 100vh;
            background-color: #f8f9fa;
          }

          .main-content {
            flex: 1;
            overflow-y: auto;
          }

          .skeleton-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 20px;
            margin-bottom: 20px;
          }

          /* Cards Grid */
          .cards-grid {
            display: grid;
            gap: 4px !important;
            margin-bottom: 20px;
            width: 100%;
          }

          /* Mobile: 1 card per row */
          @media (max-width: 768px) {
            .cards-grid {
              grid-template-columns: 1fr !important;
            }
          }

          /* Desktop: max 4 per row, for 12 cards = 4 + 4 + 4 distribution */
          @media (min-width: 769px) {
            .cards-grid {
              grid-template-columns: repeat(4, 1fr) !important;
            }
          }

          /* Full Width Chart - Products by Account */
          .full-width-chart {
            display: grid !important;
            grid-template-columns: 1fr !important;
            gap: 20px;
            margin-bottom: 24px;
            width: 100% !important;
          }

          .full-width-chart > * {
            width: 100% !important;
            min-height: 450px;
          }

          .full-width-chart canvas {
            width: 100% !important;
            height: auto !important;
          }

          /* Two Column Charts Grid - Desktop: 2 per row, Mobile: 1 per row */
          .two-col-charts-grid {
            display: grid !important;
            gap: 20px;
            margin-bottom: 24px;
            width: 100%;
          }

          .two-col-charts-grid > * {
            min-height: 400px;
            width: 100%;
          }

          /* Mobile: 1 chart per row */
          @media (max-width: 768px) {
            .two-col-charts-grid {
              grid-template-columns: 1fr !important;
            }
          }

          /* Desktop: 2 charts per row */
          @media (min-width: 769px) {
            .two-col-charts-grid {
              grid-template-columns: repeat(2, 1fr) !important;
            }
          }

          /* Ensure all chart containers are properly sized */
          .chart-card {
            width: 100% !important;
            height: auto !important;
            display: flex !important;
            flex-direction: column !important;
          }

          .chart-card canvas {
            width: 100% !important;
            height: auto !important;
          }

          /* Open Tasks Table - Updated column widths */
          .open-tasks-table {
            margin-top: 32px;
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            overflow: hidden;
          }

          .open-tasks-table-container {
            overflow-x: hidden;
            max-height: 400px;
            overflow-y: auto;
          }

          .open-tasks-table table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
          }

          .open-tasks-table th,
          .open-tasks-table td {
            padding: 12px;
            text-align: left;
            border-bottom: 1px solid #dee2e6;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .open-tasks-table th {
            background-color: #f8f9fa;
            font-weight: 600;
            position: sticky;
            top: 0;
            z-index: 10;
          }

          .open-tasks-table tbody tr:hover {
            background-color: #f8f9fa;
            cursor: pointer;
          }

          /* Updated Column widths - Details 30%, others equal */
          .open-tasks-table th:nth-child(1),
          .open-tasks-table td:nth-child(1) {
            width: 14%;
            white-space: nowrap;
          } /* Owner */
          .open-tasks-table th:nth-child(2),
          .open-tasks-table td:nth-child(2) {
            width: 14%;
            white-space: nowrap;
          } /* Account */
          .open-tasks-table th:nth-child(3),
          .open-tasks-table td:nth-child(3) {
            width: 14%;
            white-space: nowrap;
          } /* Subject */
          .open-tasks-table th:nth-child(4),
          .open-tasks-table td:nth-child(4) {
            width: 14%;
            white-space: nowrap;
          } /* Task Type */
          .open-tasks-table th:nth-child(5),
          .open-tasks-table td:nth-child(5) {
            width: 14%;
            white-space: nowrap;
          } /* Date */
          .open-tasks-table th:nth-child(6),
          .open-tasks-table td:nth-child(6) {
            width: 30%;
            white-space: normal;
            word-wrap: break-word;
          } /* Details */

          /* Mobile responsiveness */
          @media (max-width: 768px) {
            .open-tasks-table th,
            .open-tasks-table td {
              padding: 8px;
              font-size: 14px;
            }
          }
        `}
      </style>
      <div className="container">
        {sidebar !== "0" ? <Sidebar /> : null}

        {!error.global && (
          <div
            className="content dashboard main-content"
            key={`dashboard-${selectedDirector || "all"}`}
          >
            {/* Header Section */}
            <div style={{ padding: "20px 20px 0 20px" }}>
              {/* User Filter Section */}
              <div
                style={{
                  marginBottom: "20px",
                  display: "flex",
                  alignItems: "center",
                  gap: "20px",
                }}
              >
                <h1 className="cs-title" style={{ margin: 0 }}>
                  Directors Dashboard
                </h1>
                <UserFilter
                  users={directors}
                  selectedUser={selectedDirector}
                  onUserChange={handleDirectorChange}
                  loading={loadingDirectors}
                  userType="Director"
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr) 2fr",
                  gap: "12px",
                  marginBottom: "20px",
                }}
              >
                <a
                  href="https://gargleinc.lightning.force.com/lightning/r/Report/00ORo000003fqQjMAI/view"
                  target="_blank"
                  className="btn btn-light btn-reports"
                  rel="noopener noreferrer"
                  style={{ textAlign: "center", padding: "12px 8px" }}
                >
                  BOB Alex
                </a>
                <a
                  href="https://gargleinc.lightning.force.com/lightning/r/Report/00ORo000005d2BzMAI/view"
                  target="_blank"
                  className="btn btn-light btn-reports"
                  rel="noopener noreferrer"
                  style={{ textAlign: "center", padding: "12px 8px" }}
                >
                  BOB Rikelle
                </a>
                <a
                  href="https://gargleinc.lightning.force.com/lightning/r/Report/00ORo000005d4GzMAI/view"
                  target="_blank"
                  className="btn btn-light btn-reports"
                  rel="noopener noreferrer"
                  style={{ textAlign: "center", padding: "12px 8px" }}
                >
                  BOB Ryan
                </a>
              </div>
            </div>

            {/* Status Cards Section */}
            <div style={{ padding: "0 20px 20px 20px" }}>
              <div className="cards-grid">
                <SimpleStatCard
                  title="Active Memberships"
                  value={data.activeMemberships}
                  loading={false}
                  error={error.activeMemberships}
                  onRetry={() => handleRetry("activeMemberships")}
                  color="green"
                  tooltip="Membership Orders with Status = Activated"
                  onClick={() => handleChartClick("activeMemberships", "all")}
                  style={{ padding: "16px 12px" }}
                  summary="Customer and Customer (CA) accounts"
                />

                <SimpleStatCard
                  title="Overdue Tasks"
                  value={data.overdueTasks}
                  loading={false}
                  error={error.overdueTasks}
                  onRetry={() => handleRetry("overdueTasks")}
                  color={data.overdueTasks?.count > 0 ? "red" : "green"}
                  tooltip="Tasks for Active accounts with Status = Open and Date < today"
                  onClick={() =>
                    openURLInNewTab(
                      "https://gargleinc.lightning.force.com/lightning/r/Report/00OEY000000KCr32AG/view"
                    )
                  }
                  style={{ padding: "16px 12px" }}
                  summary="Past due tasks requiring attention"
                />

                <SimpleStatCard
                  title="Accts. No Planned Task"
                  value={data.noOpenActivities}
                  loading={false}
                  error={error.noOpenActivities}
                  onRetry={() => handleRetry("noOpenActivities")}
                  color={data.noOpenActivities?.count > 0 ? "red" : "green"}
                  tooltip="Accounts with no Tasks with Status = Open"
                  onClick={() =>
                    openURLInNewTab(
                      "https://gargleinc.lightning.force.com/lightning/r/Report/00OEY000000Jcv42AC/view"
                    )
                  }
                  style={{ padding: "16px 12px" }}
                  summary="Client accounts needing task planning"
                />

                <SimpleStatCard
                  title="DAR Tasks This Month"
                  value={data.darTasksThisMonth}
                  loading={false}
                  error={error.darTasksThisMonth}
                  onRetry={() => handleRetry("darTasksThisMonth")}
                  color={
                    selectedDirector
                      ? data.darTasksThisMonth?.count >= 60
                        ? "green"
                        : data.darTasksThisMonth?.count >= 30
                        ? "yellow"
                        : "red"
                      : "green"
                  }
                  tooltip="DAR tasks completed this month (need 60+ per director for green)"
                  onClick={() => handleChartClick("darTasksThisMonth", "all")}
                  style={{ padding: "16px 12px" }}
                  summary="Director Account Review tasks completed"
                />

                <SimpleStatCard
                  title="DAR Tasks This Week"
                  value={data.darTasksThisWeek}
                  loading={false}
                  error={error.darTasksThisWeek}
                  onRetry={() => handleRetry("darTasksThisWeek")}
                  color={
                    selectedDirector ? data.darTasksThisWeek?.color : "green"
                  }
                  tooltip="DAR tasks completed this week (need 15+ per director for green)"
                  onClick={() => handleChartClick("darTasksThisWeek", "all")}
                  style={{ padding: "16px 12px" }}
                  summary="Weekly Director Account Review tasks"
                />

                <SimpleStatCard
                  title="Upsells This Month"
                  value={{
                    count: data.upsellsThisMonth?.count || 0,
                    subtitle: data.upsellsThisMonth?.totalAmount
                      ? `$${data.upsellsThisMonth.totalAmount.toLocaleString()}`
                      : "$0",
                  }}
                  loading={false}
                  error={error.upsellsThisMonth}
                  onRetry={() => handleRetry("upsellsThisMonth")}
                  color={data.upsellsThisMonth?.color || "red"}
                  tooltip="Closed Won upsell opportunities this month (1+ for green)"
                  onClick={() => handleChartClick("upsellsThisMonth", "all")}
                  style={{ padding: "16px 12px" }}
                  summary="Closed upsell opportunities revenue"
                />

                <SimpleStatCard
                  title="Tasks Completed This Week"
                  value={data.tasksCompletedThisWeek}
                  loading={false}
                  error={error.tasksCompletedThisWeek}
                  onRetry={() => handleRetry("tasksCompletedThisWeek")}
                  color="green"
                  tooltip="Tasks completed in the current week"
                  style={{ padding: "16px 12px" }}
                  summary="All task types completed this week"
                />

                <SimpleStatCard
                  title="Tasks Completed This Month"
                  value={data.tasksCompletedThisMonth}
                  loading={false}
                  error={error.tasksCompletedThisMonth}
                  onRetry={() => handleRetry("tasksCompletedThisMonth")}
                  color="green"
                  tooltip="Tasks completed in the current month"
                  style={{ padding: "16px 12px" }}
                  summary="All task types completed this month"
                />

                {/* New Cards */}
                <SimpleStatCard
                  title="New Clients This Month"
                  value={data.newClientsThisMonth}
                  loading={false}
                  error={error.newClientsThisMonth}
                  onRetry={() => handleRetry("newClientsThisMonth")}
                  color="green"
                  tooltip="GM orders activated this month"
                  onClick={() => handleChartClick("newClientsThisMonth", "all")}
                  style={{ padding: "16px 12px" }}
                  summary="GM orders activated this month"
                />

                <SimpleStatCard
                  title="No Up-sell Discussion"
                  value={data.noUpSellDiscussion}
                  loading={false}
                  error={error.noUpSellDiscussion}
                  onRetry={() => handleRetry("noUpSellDiscussion")}
                  color={
                    data.noUpSellDiscussion?.count > 0 ? "yellow" : "green"
                  }
                  tooltip="Accounts active 90+ days without up-sell discussion"
                  onClick={() => handleChartClick("noUpSellDiscussion", "all")}
                  style={{ padding: "16px 12px" }}
                  summary="Accounts needing up-sell discussion"
                />

                <SimpleStatCard
                  title="No Payment Confirmed"
                  value={data.noPaymentConfirmed}
                  loading={false}
                  error={error.noPaymentConfirmed}
                  onRetry={() => handleRetry("noPaymentConfirmed")}
                  color={data.noPaymentConfirmed?.count > 0 ? "red" : "green"}
                  tooltip="Customer accounts without valid payment method"
                  onClick={() => handleChartClick("noPaymentConfirmed", "all")}
                  style={{ padding: "16px 12px" }}
                  summary="Customer accounts missing payment"
                />

                <SimpleStatCard
                  title="High Risk"
                  value={data.highRiskAccounts}
                  loading={false}
                  error={error.highRiskAccounts}
                  onRetry={() => handleRetry("highRiskAccounts")}
                  color={data.highRiskAccounts?.color || "green"}
                  tooltip="Accounts marked as High Risk for cancellation"
                  onClick={() => handleChartClick("highRiskAccounts", "all")}
                  style={{ padding: "16px 12px" }}
                  summary="Accounts at high risk of cancellation"
                />
              </div>

              {/* Full Width Chart - Accounts by Product */}
              <div className="full-width-chart" key="full-width">
                <ChartCard
                  key="accountsByProduct"
                  title="Accounts by Product Distribution"
                  loading={false}
                  error={error.accountsByProduct}
                  onRetry={() => handleRetry("accountsByProduct")}
                  onClick={() => handleChartClick("accountsByProduct", "all")}
                >
                  {processedChartData.accountsByProduct ? (
                    <DMStackedChart
                      key={`accountsByProduct-${selectedDirector || "all"}-${
                        lastDataHashRef.current
                      }`}
                      data={processedChartData.accountsByProduct}
                      width="100%"
                      height={400}
                      onClick={(product) =>
                        handleChartClick("accountsByProduct", product)
                      }
                      darkFont={true}
                      title="Accounts by Product Distribution"
                    />
                  ) : null}
                  <div
                    style={{
                      padding: "12px",
                      textAlign: "center",
                      fontSize: "14px",
                      color: "#666",
                      borderTop: "1px solid #eee",
                    }}
                  >
                    Distribution of active accounts across product types
                  </div>
                </ChartCard>
              </div>

              {/* Two Column Charts Grid - Row 1 */}
              <div className="two-col-charts-grid" key="row-1">
                {/* Upcoming GM Order Expirations Chart */}
                <ChartCard
                  key="upcomingGMOrderExpirations"
                  title="Upcoming GM Order Expirations"
                  loading={false}
                  error={error.upcomingGMOrderExpirations}
                  onRetry={() => handleRetry("upcomingGMOrderExpirations")}
                  onClick={() =>
                    handleChartClick("upcomingGMOrderExpirations", "all")
                  }
                >
                  {processedChartData.upcomingGMOrderExpirations ? (
                    <DMStackedChart
                      key={`upcomingGMOrderExpirations-${
                        selectedDirector || "all"
                      }-${lastDataHashRef.current}`}
                      data={processedChartData.upcomingGMOrderExpirations}
                      width="100%"
                      height={350}
                      onClick={(color) =>
                        handleChartClick("upcomingGMOrderExpirations", color)
                      }
                      darkFont={true}
                      title="Upcoming GM Order Expirations"
                    />
                  ) : null}
                  <div
                    style={{
                      padding: "12px",
                      textAlign: "center",
                      fontSize: "14px",
                      color: "#666",
                      borderTop: "1px solid #eee",
                    }}
                  >
                    GM orders by time until expiration
                  </div>
                </ChartCard>

                {/* Paused Products Chart */}
                <ChartCard
                  key="pausedProducts"
                  title="Paused Products Status"
                  loading={false}
                  error={error.pausedProducts}
                  onRetry={() => handleRetry("pausedProducts")}
                  onClick={() => handleChartClick("pausedProducts", "all")}
                >
                  {processedChartData.pausedProducts ? (
                    <DMStackedChart
                      key={`pausedProducts-${selectedDirector || "all"}-${
                        lastDataHashRef.current
                      }`}
                      data={processedChartData.pausedProducts}
                      width="100%"
                      height={350}
                      onClick={(color) =>
                        handleChartClick("pausedProducts", color)
                      }
                      darkFont={true}
                      title="Paused Products Status"
                    />
                  ) : null}
                  <div
                    style={{
                      padding: "12px",
                      textAlign: "center",
                      fontSize: "14px",
                      color: "#666",
                      borderTop: "1px solid #eee",
                    }}
                  >
                    Products on pause by expiration timeline
                  </div>
                </ChartCard>
              </div>

              {/* Two Column Charts Grid - Row 2 */}
              <div className="two-col-charts-grid" key="row-2">
                {/* Overdue Invoices Chart */}
                <ChartCard
                  key="overdueInvoices"
                  title="Overdue Invoices Status"
                  loading={false}
                  error={error.overdueInvoices}
                  onRetry={() => handleRetry("overdueInvoices")}
                  onClick={() => handleChartClick("overdueInvoices", "all")}
                >
                  {processedChartData.overdueInvoices ? (
                    <DMStackedChart
                      key={`overdueInvoices-${selectedDirector || "all"}-${
                        lastDataHashRef.current
                      }`}
                      data={processedChartData.overdueInvoices}
                      width="100%"
                      height={350}
                      onClick={(color) =>
                        handleChartClick("overdueInvoices", color)
                      }
                      darkFont={true}
                      title="Overdue Invoices Status"
                    />
                  ) : null}
                  <div
                    style={{
                      padding: "12px",
                      textAlign: "center",
                      fontSize: "14px",
                      color: "#666",
                      borderTop: "1px solid #eee",
                    }}
                  >
                    Invoices by overdue status and timeline
                  </div>
                </ChartCard>

                {/* SAI Tasks Completion Chart */}
                <ChartCard
                  key="completionOfSAItasks"
                  title="SAI Tasks Completion"
                  loading={false}
                  error={error.completionOfSAItasks}
                  onRetry={() => handleRetry("completionOfSAItasks")}
                  onClick={() =>
                    handleChartClick("completionOfSAItasks", "all")
                  }
                >
                  {processedChartData.completionOfSAItasks ? (
                    <DMStackedChart
                      key={`completionOfSAItasks-${selectedDirector || "all"}-${
                        lastDataHashRef.current
                      }`}
                      data={processedChartData.completionOfSAItasks}
                      width="100%"
                      height={350}
                      onClick={(color) =>
                        handleChartClick("completionOfSAItasks", color)
                      }
                      darkFont={true}
                      title="SAI Tasks Completion"
                    />
                  ) : null}
                  <div
                    style={{
                      padding: "12px",
                      textAlign: "center",
                      fontSize: "14px",
                      color: "#666",
                      borderTop: "1px solid #eee",
                    }}
                  >
                    Strategic Account Initiative task completion status
                  </div>
                </ChartCard>
              </div>

              {/* Open Tasks Table */}
              {openTasks && openTasks.length > 0 && (
                <div className="open-tasks-table">
                  <div
                    style={{
                      padding: "20px 20px 10px 20px",
                      backgroundColor: "#f8f9fa",
                      borderBottom: "1px solid #dee2e6",
                    }}
                  >
                    <h4 style={{ margin: 0, color: "#333" }}>
                      Open Tasks{" "}
                      {selectedDirector
                        ? `for ${
                            directors.find((d) => d.id === selectedDirector)
                              ?.name || "Selected Director"
                          }`
                        : ""}
                    </h4>
                    <p
                      style={{
                        margin: "5px 0 0 0",
                        color: "#666",
                        fontSize: "14px",
                      }}
                    >
                      Click on any task to open in Salesforce
                    </p>
                  </div>

                  {/* Group tasks by user */}
                  {(() => {
                    const tasksByUser = {};
                    openTasks.forEach((task) => {
                      if (!tasksByUser[task.Owner]) {
                        tasksByUser[task.Owner] = [];
                      }
                      tasksByUser[task.Owner].push(task);
                    });

                    return Object.entries(tasksByUser).map(
                      ([userName, userTasks], userIndex) => (
                        <div key={userName}>
                          <div
                            style={{
                              padding: "10px 20px",
                              backgroundColor: "#2b5597",
                              color: "white",
                              marginTop: userIndex === 0 ? "0" : "10px",
                              fontWeight: "600",
                              textTransform: "uppercase",
                            }}
                          >
                            {userName} - <b>{userTasks.length}</b> Tasks
                          </div>
                          <div className="open-tasks-table-container">
                            <table>
                              <thead>
                                <tr>
                                  <th>Account</th>
                                  <th>Subject</th>
                                  <th>Task Type</th>
                                  <th>Date</th>
                                  <th>Details</th>
                                </tr>
                              </thead>
                              <tbody>
                                {userTasks.map((task, index) => (
                                  <tr
                                    key={index}
                                    onClick={() => {
                                      const salesforceUrl = `https://gargleinc.lightning.force.com/lightning/r/Task/${task.Id}/view`;
                                      window.open(salesforceUrl, "_blank");
                                    }}
                                  >
                                    <td>{task.AccountName}</td>
                                    <td>{task.Subject}</td>
                                    <td>{task.TaskType}</td>
                                    <td>
                                      {task.Date
                                        ? new Date(
                                            task.Date
                                          ).toLocaleDateString()
                                        : "-"}
                                    </td>
                                    <td>{task.Details}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DirectorsDashboard;
